// Risk-score league table across a single meta dimension on the summary
// page. Picks a dimension (department, location, city, country, company,
// state) from a dropdown and renders the top 10 values ranked
// by risk score as horizontal bars coloured by the same RAG bands the
// risk dial uses.
//
// Calculations: precomputed aggregates from data.js, no per-render math.
//
// Integration hook: `metaRiskApi.useLeagueTableQuery({ dimension })` once
// the dimensional risk views land.
import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Menu, MenuItem, Button, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'motion/react';
import MainCard from '../../../../components/cards/MainCard';
import { DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES } from '../../data';
import { bandFor } from '../../riskScore';
import useDashboardData from '../../hooks/useDashboardData';
import { hasTenancyScope } from '../../serverScope';

// Order here is the order shown in the dropdown.
const DIMENSIONS = [
    // Live-binding getters: refresh after each data hydration.
    { id: 'department', label: 'Department', get source() { return DEPTS; },     drillType: 'department' },
    { id: 'location',   label: 'Location',   get source() { return LOCS; },      drillType: 'location' },
    { id: 'city',       label: 'City',       get source() { return CITIES; },    drillType: 'city' },
    { id: 'country',    label: 'Country',    get source() { return COUNTRIES; }, drillType: 'country' },
    { id: 'company',    label: 'Company',    get source() { return COMPANIES; }, drillType: 'company' },
    { id: 'state',      label: 'State',      get source() { return STATES; },    drillType: 'state' },
];

const ROW_LIMIT = 5;

const fadeUp = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };

// Local copy of the ShimmerBar pattern from CoursesPage so this card matches
// the existing bar-chart language without exporting it across files.
function ShimmerBar({ color, pct, delay, isDark }) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);
    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height: 18, borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    background: color,
                    transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${delay}s`,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '-100%',
                        width: '100%', height: '100%',
                        background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)'}, transparent)`,
                        animation: value > 0 ? 'shine 2s ease-in-out' : 'none',
                        animationDelay: `${delay + 0.5}s`,
                        borderRadius: 1,
                    },
                },
                overflow: 'hidden',
                '@keyframes shine': { '0%': { left: '-100%' }, '100%': { left: '100%' } },
            }}
        />
    );
}

export default function MetaRiskLeagueCard({ drill, scope }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [dimensionId, setDimensionId] = useState('department');
    const [anchorEl, setAnchorEl] = useState(null);

    const dimension = DIMENSIONS.find((d) => d.id === dimensionId) || DIMENSIONS[0];
    // Risk-by-dimension from the rollup (leagues); falls back to the seed lists.
    const leaguesQ = useDashboardData({ metric: 'simulations', aggregation: 'leagues', scope });
    const aggByDim = useMemo(() => {
        const rs = leaguesQ.data?.leagues;
        if (!Array.isArray(rs)) return null;
        const m = {};
        for (const r of rs) { (m[r.dimType] || (m[r.dimType] = [])).push({ name: r.name, risk: Math.round(r.avgRisk), users: r.users }); }
        return m;
    }, [leaguesQ.data]);
    const tenancyScoped = hasTenancyScope(scope);
    const rows = useMemo(() => {
        const agg = aggByDim && aggByDim[dimension.id];
        const src = (agg && agg.length) ? agg : (tenancyScoped && aggByDim ? [] : dimension.source);
        return [...src].sort((a, b) => b.risk - a.risk).slice(0, ROW_LIMIT);
    }, [dimension, aggByDim, tenancyScoped]);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        League Table
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Top {rows.length} {dimension.label.toLowerCase()}s ranked by risk - click a row to drill in
                    </Typography>
                </Box>
                <>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{
                            borderRadius: '4px',
                            borderColor: 'secondary.main',
                            color: 'secondary.main',
                            fontSize: 11,
                            py: '3px',
                            px: '10px',
                            textTransform: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: 'secondary.main', borderColor: 'secondary.main', color: 'common.white' },
                        }}
                    >
                        {dimension.label}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { border: '1px solid', borderColor: 'secondary.main', borderRadius: '4px', boxShadow: 'none', mt: 0.5 } }}
                    >
                        {DIMENSIONS.map((d) => (
                            <MenuItem
                                key={d.id}
                                onClick={() => { setDimensionId(d.id); setAnchorEl(null); }}
                                sx={{
                                    fontSize: 11,
                                    borderRadius: '4px', mx: 0.5,
                                    color: d.id === dimensionId ? 'common.white' : 'secondary.main',
                                    backgroundColor: d.id === dimensionId ? 'secondary.main' : 'transparent',
                                    '&:hover': { backgroundColor: 'secondary.main', color: 'common.white' },
                                }}
                            >
                                {d.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            </Box>

            <Box sx={{ flex: 1, px: 3, pt: 2, pb: 2.5, display: 'flex', flexDirection: 'column' }}>
                <Box
                    component={motion.div}
                    variants={stagger}
                    initial="hidden"
                    animate="visible"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
                >
                    {tenancyScoped && rows.length === 0 && (
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            No {dimension.label.toLowerCase()}s with scored users in the selected tenancies
                        </Typography>
                    )}
                    {rows.map((r, i) => {
                        const band = bandFor(r.risk);
                        const barColor = band?.color || theme.palette.primary.main;
                        return (
                            <Box
                                key={r.name}
                                component={motion.div}
                                variants={fadeUp}
                                role="button"
                                tabIndex={0}
                                onClick={() => drill && drill('meta-detail', `${dimension.label} - ${r.name}`, { type: dimension.drillType, value: r.name })}
                                onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && drill) {
                                        e.preventDefault();
                                        drill('sims', `Sims - ${r.name}`, { type: dimension.drillType, value: r.name });
                                    }
                                }}
                                sx={{
                                    cursor: 'pointer', borderRadius: '4px', px: 0.5, py: 0.25,
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6, gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', width: 16, flexShrink: 0 }}>
                                            {i + 1}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {r.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                            {r.users.toLocaleString()} users
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', width: 32, textAlign: 'right' }}>
                                            {r.risk}
                                        </Typography>
                                    </Box>
                                </Box>
                                <ShimmerBar color={barColor} pct={r.risk} delay={i * 0.08} isDark={isDark} />
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ pt: 1.5, mt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5, fontSize: 9, color: 'text.disabled', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#66BB6A' }} />
                        Low 0-29
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#FFCA28' }} />
                        Medium 30-55
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#FF7043' }} />
                        High 56-75
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#FF5252' }} />
                        Critical 76+
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}
